/* Mobile product-only header mode. Scroll handling reads cached boundaries,
   never measures the gallery on each scroll. Zepto keeps ownership of editing. */
(() => {
  let dispose = () => {};
  let activeProduct = null;
  const mobile = matchMedia('(max-width: 749px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  function mount() {
    const product = document.querySelector('product-info[id^="MainProduct-"]');
    if (mobile.matches && product && product === activeProduct && product.isConnected) return;
    dispose();
    const container = product?.querySelector('.product__sticky-container');
    const media = container?.querySelector('.product__media-wrapper');
    const header = document.querySelector('.section-header');
    const wrapper = header?.querySelector('.header-wrapper');
    if (!mobile.matches || !product?.querySelector('.product-personalizer') || !media || !wrapper || getComputedStyle(media).position !== 'sticky') return;
    activeProduct = product;
    const body = document.body;
    const abort = new AbortController();
    const options = { signal: abort.signal, passive: true };
    let start = Infinity, end = -Infinity, frame = 0, measuring = false, compositionFrame = 0;
    let overlay = false, animation = null, stopped = false;
    let panelWasOpen = false, restorePanel = false, revision = 0;
    const savedSticky = header.classList.contains('shopify-section-header-sticky');
    // Preserve fractional pixels: offsetHeight rounds up and can expose a white seam.
    header.style.setProperty('--bw-header-layout-height', `${header.getBoundingClientRect().height}px`);
    // Fade the background and controls independently; never fade the logo's ancestor.
    const backdrop = document.createElement('span');
    backdrop.className = 'bw-zepto-header-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.style.background = getComputedStyle(wrapper).background;
    wrapper.prepend(backdrop);
    // Icons change colour through CSS; the logo and interactive controls
    // remain visible throughout. Only this independent paint layer fades.
    body.classList.add('bw-zepto-header-enabled');
    header.classList.add('shopify-section-header-sticky');
    header.classList.remove('shopify-section-header-hidden');
    const panelOpen = () => !!header.querySelector('details[open]');
    const locked = () => panelOpen() ||
      // Only lock focus on controls that overlay mode would hide. Menu, search
      // and cart keep focus after dismissal and must not freeze scroll state.
      !!document.activeElement?.closest('.header__icons > :not(details-modal):not(.header__icon--cart)');
    const composing = () => {
      const controller = window.MobileProductController;
      return controller && product.contains(controller.activeInput) &&
        (controller.state === 'EDITING' || controller.state === 'ANCHORED' || controller.isDismissing);
    };
    // Follow the rendered preview, not just the keyboard viewport. Keep controls
    // at the visible image edge if its top extends above the viewport.
    function syncCompositionPosition() {
      if (composing() && !panelOpen()) {
        const viewTop = window.visualViewport?.offsetTop || 0;
        // Before the first placement the photo is still in document flow.
        // Do not drag the header down to it while Safari opens the keyboard.
        const imageTop = window.MobileProductController?.hasPlacement
          ? media.getBoundingClientRect().top : viewTop;
        header.style.setProperty('--bw-zepto-header-top', `${Math.max(viewTop, imageTop)}px`);
      } else {
        header.style.removeProperty('--bw-zepto-header-top');
      }
    }
    function followDismissal() {
      compositionFrame = 0;
      if (stopped) return;
      syncCompositionPosition();
      // The reference text flow also animates CSS top when no page scroll is
      // needed. Follow that animation without creating another scroll owner.
      if (composing() && window.MobileProductController?.isDismissing) {
        compositionFrame = requestAnimationFrame(followDismissal);
      }
    }
    function apply(next) {
      overlay = next;
      body.classList.toggle('bw-zepto-header-overlay', next);
      document.dispatchEvent(new CustomEvent('bw:header-overlay-change'));
      // Composition already uses the shared overlay origin. Header painting
      // must never start another page-scroll or preview-positioning animation.
    }
    function cancelFade() {
      revision++;
      animation?.cancel();
      animation = null;
    }
    function targetState() {
      // The personaliser owns a pinned preview while editing, even
      // when focus started at the top of the page before the scroll boundary.
      if (composing()) return true;
      const y = window.scrollY;
      // Enter slightly inside the range; leave exactly at its real edge.
      // Never retain a transparent header above an image that is not pinned.
      return overlay ? y >= start && y < end : y >= start + 2 && y < end - 2;
    }
    function update() {
      if (stopped) return;
      syncCompositionPosition();
      if (locked()) return;
      const wanted = targetState();
      if (wanted === overlay && !restorePanel) return;
      // Read the currently rendered opacity before cancelling. Rapid reversals
      // continue from this value rather than finishing a stale out/in sequence.
      const from = getComputedStyle(backdrop).opacity;
      cancelFade();
      apply(wanted);
      restorePanel = false;
      body.classList.remove('bw-zepto-header-panel');
      if (reduced.matches || !backdrop.animate) return;
      const to = getComputedStyle(backdrop).opacity;
      if (Math.abs(Number(from) - Number(to)) < .001) return;
      const token = revision;
      const effect = backdrop.animate([{ opacity: from }, { opacity: to }], {
        duration: 240, easing: 'cubic-bezier(.25, .1, .25, 1)'
      });
      animation = effect;
      effect.finished.then(() => {
        if (stopped || token !== revision) return;
        animation = null;
      }).catch(() => {});
    }
    function syncPanel() {
      const open = panelOpen();
      syncCompositionPosition();
      if (open) {
        cancelFade();
        panelWasOpen = true;
        restorePanel = false;
        body.classList.add('bw-zepto-header-panel');
      } else if (panelWasOpen) {
        // Native menu removes `open` only after its closing animation. Search
        // removes it synchronously. Both paths restore focus before this point.
        panelWasOpen = false;
        restorePanel = true;
        cancelFade();
        queue(true);
      }
    }
    function queue(measure = false) {
      measuring ||= measure;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (measuring && !locked()) {
          const rect = container.getBoundingClientRect();
          const safe = parseFloat(getComputedStyle(media).getPropertyValue('--bw-safe-top')) || 0;
          start = rect.top + scrollY - safe;
          end = rect.bottom + scrollY - media.offsetHeight - safe;
          measuring = false;
        }
        update();
      });
    }
    reduced.addEventListener('change', () => {
      if (reduced.matches) { cancelFade(); queue(); }
    }, { signal: abort.signal });
    const resize = new ResizeObserver(() => queue(true));
    resize.observe(container); resize.observe(media); resize.observe(header);
    // Recheck when the containing section enters/leaves the viewport.
    const boundaries = new IntersectionObserver(() => queue(true));
    boundaries.observe(container);
    document.addEventListener('bw:zepto-composition', event => {
      syncCompositionPosition();
      if (event.detail?.phase === 'dismiss' && !compositionFrame) {
        compositionFrame = requestAnimationFrame(followDismissal);
      }
      // Start the existing fade directly on field focus; position events only
      // move its anchor and never restart a fade or move the page.
      if (event.detail?.phase !== 'position') update();
    }, options);
    window.addEventListener('scroll', () => queue(), options);
    window.addEventListener('resize', () => queue(true), options);
    window.visualViewport?.addEventListener('resize', () => queue(true), options);
    // Safari pans the visual viewport separately from document scrolling during
    // focus. Refresh this paint anchor too, without moving the page or canvas.
    window.visualViewport?.addEventListener('scroll', () => queue(), options);
    document.addEventListener('focusout', () => queue(true), options);
    header.addEventListener('focusin', () => { if (locked()) cancelFade(); }, options);
    // Observe native open state, not clicks: covers Escape, backdrop and close buttons.
    const panels = new MutationObserver(syncPanel);
    panels.observe(header, { subtree: true, attributes: true, attributeFilter: ['open'] });
    syncPanel();
    // The existing Zepto controller changes body classes when editing finishes.
    const editing = new MutationObserver(() => queue());
    editing.observe(body, { attributes: true, attributeFilter: ['class'] });
    queue(true);
    dispose = () => {
      activeProduct = null;
      stopped = true; abort.abort(); resize.disconnect(); boundaries.disconnect(); editing.disconnect(); panels.disconnect();
      cancelAnimationFrame(frame); cancelAnimationFrame(compositionFrame); cancelFade();
      body.classList.remove('bw-zepto-header-enabled', 'bw-zepto-header-overlay', 'bw-zepto-header-panel');
      backdrop.remove();
      header.style.removeProperty('--bw-header-layout-height');
      header.style.removeProperty('--bw-zepto-header-top');
      if (!savedSticky) header.classList.remove('shopify-section-header-sticky');
      header.querySelector('sticky-header')?.setHeaderHeight();
    };
  }
  mobile.addEventListener('change', mount);
  window.addEventListener('pplrAppBuild', mount);
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.contains(activeProduct) || event.target.classList.contains('section-header')) dispose();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();
